import { useState, useCallback } from 'react';
import styles from './ApiDocsPage.module.css';

const CSV_COLUMNS = [
  { name: 'MEMBER_ID', type: 'string', required: 'Required*', description: 'External member identifier. Required for UPDATE, CANCEL, TERMINATE. Optional for ADD.' },
  { name: 'ACTION', type: 'string', required: 'Required', description: 'Operation type: ADD, UPDATE, CANCEL, or TERMINATE' },
  { name: 'FIRST_NAME', type: 'string', required: 'Required\u2020', description: 'Member first name (required for ADD)' },
  { name: 'LAST_NAME', type: 'string', required: 'Required\u2020', description: 'Member last name (required for ADD)' },
  { name: 'EMAIL', type: 'string', required: 'Required\u2020', description: 'Member email address (required for ADD; identifier for UPDATE/CANCEL/TERMINATE)' },
  { name: 'PHONE', type: 'string', required: 'Optional', description: 'Phone number' },
  { name: 'DOB', type: 'string', required: 'Optional', description: 'Date of birth (MM/DD/YYYY or YYYY-MM-DD)' },
  { name: 'GENDER', type: 'string', required: 'Optional', description: 'M, F, or O (mapped to male, female, other)' },
  { name: 'ADDRESS', type: 'string', required: 'Optional', description: 'Street address' },
  { name: 'CITY', type: 'string', required: 'Optional', description: 'City' },
  { name: 'STATE', type: 'string', required: 'Optional', description: 'State abbreviation (e.g., TX, CA)' },
  { name: 'ZIP', type: 'string', required: 'Optional', description: 'US zip code (e.g., 78701)' },
  { name: 'PLAN_CODE', type: 'string', required: 'Optional', description: 'Membership plan code. Common codes: ACCESS, PLUS, ONE, ACCESS_FAMILY, PLUS_FAMILY, ONE_FAMILY, PLUS_MEMBER_SPOUSE, ONE_MEMBER_CHILD. Use GET /api/partner/plan-codes for all available codes. Defaults to ACCESS.' },
  { name: 'AGENCY_NAME', type: 'string', required: 'Optional', description: 'Agency or TPA name for reference' },
  { name: 'EFFECTIVE_DATE', type: 'string', required: 'Optional', description: 'When the action takes effect (MM/DD/YYYY or YYYY-MM-DD)' },
  { name: 'NEW_PLAN_CODE', type: 'string', required: 'Optional', description: 'New plan code for plan changes (used with UPDATE action). Use GET /api/partner/plan-codes for available codes.' },
];

const ACTION_TYPES = [
  { action: 'ADD', description: 'Creates a new user account and profile. If the email already exists, the row is skipped (not an error). Assigns the member to the uploading agency. Requires FIRST_NAME, LAST_NAME, and EMAIL.' },
  { action: 'UPDATE', description: 'Finds an existing member by MEMBER_ID or EMAIL and updates their profile. Only non-empty fields are applied. Use NEW_PLAN_CODE to change subscription plans. Requires at least one identifier.' },
  { action: 'CANCEL', description: 'Sets the member\'s account to inactive (is_active = false). Profile data and subscriptions are preserved for potential reactivation. Requires MEMBER_ID or EMAIL.' },
  { action: 'TERMINATE', description: 'Deactivates the member account and cancels all active subscriptions. Use for full removal from the platform. Requires MEMBER_ID or EMAIL.' },
];

const UPLOAD_STATUSES = [
  { status: 'pending', description: 'Upload record created, awaiting processing' },
  { status: 'processing', description: 'File is being parsed and processed row-by-row' },
  { status: 'completed', description: 'All rows processed — check succeeded/failed/skipped counts for results' },
  { status: 'failed', description: 'Upload failed entirely (invalid file format, internal error, or missing required headers)' },
];

const ERROR_CODES = [
  { code: '400', description: 'Bad request — no file uploaded, empty CSV, invalid headers, or row processing errors' },
  { code: '401', description: 'Unauthorized — missing or invalid X-API-Key header' },
  { code: '429', description: 'Rate limited — exceeded 100 requests per minute per API key' },
  { code: '500', description: 'Internal server error — contact support if persistent' },
];

const EXAMPLE_CSV = `MEMBER_ID,ACTION,FIRST_NAME,LAST_NAME,EMAIL,PHONE,DOB,GENDER,ADDRESS,CITY,STATE,ZIP,PLAN_CODE,AGENCY_NAME,EFFECTIVE_DATE,NEW_PLAN_CODE
,ADD,Jane,Smith,jane.smith@example.com,5551234567,05/15/1990,F,123 Main St,Austin,TX,78701,PLUS,Acme Benefits,05/01/2026,
MEM-001,UPDATE,,,jane.smith@example.com,5559876543,,,,,,,,,,PREMIUM
MEM-002,CANCEL,,,john.doe@example.com,,,,,,,,,,05/15/2026,
MEM-003,TERMINATE,,,alex.jones@example.com,,,,,,,,,,05/15/2026,`;

const CURL_EXAMPLE = `curl -X POST https://ovalcare.com/api/eligibility/upload \\
  -H "X-API-Key: your-api-key-here" \\
  -H "Content-Type: multipart/form-data" \\
  -F "file=@members.csv"`;

const SUCCESS_RESPONSE = `{
  "success": true,
  "uploadId": "a1b2c3d4-e5f6-...",
  "processed": 4,
  "succeeded": 3,
  "failed": 1,
  "skipped": 0,
  "errors": [
    { "row": 5, "message": "User not found (MEMBER_ID: MEM-003, EMAIL: alex.jones@example.com)" }
  ]
}`;

const ERROR_RESPONSE_NO_FILE = `{
  "success": false,
  "error": "No file uploaded"
}`;

const ERROR_RESPONSE_HEADERS = `{
  "success": false,
  "error": "Invalid CSV headers. Missing columns: ACTION, EMAIL",
  "missingColumns": ["ACTION", "EMAIL"]
}`;

const ERROR_RESPONSE_AUTH = `{
  "success": false,
  "error": "Missing X-API-Key header"
}`;

const ERROR_RESPONSE_RATE_LIMIT = `{
  "error": "Partner API rate limit exceeded. Max 100 requests per minute.",
  "code": "RATE_LIMITED"
}`;

const ERROR_RESPONSE_INTERNAL = `{
  "success": false,
  "error": "Internal server error"
}`;

const CodeBlock = ({ children, label }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    const text = typeof children === 'string' ? children : '';
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
    } else {
      const ta = document.createElement('textarea');
      ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    }
  }, [children]);

  return (
    <div className={styles.codeBlock}>
      <div className={styles.codeHeader}>
        {label && <span className={styles.codeLabel}>{label}</span>}
        <button className={styles.copyBtn} onClick={handleCopy} type="button">{copied ? 'Copied' : 'Copy'}</button>
      </div>
      <pre className={styles.codePre}><code>{children}</code></pre>
    </div>
  );
};

const EligibilityDocsPage = () => (
  <div className={styles.container}>
    <div className={styles.content}>
      <header className={styles.header}>
        <div className={styles.badge}>Eligibility API</div>
        <h1 className={styles.title}>Eligibility File Upload</h1>
        <p className={styles.subtitle}>
          Upload CSV files to manage member eligibility in bulk — add, update, cancel, or terminate members.
        </p>
      </header>

      {/* Overview */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Overview</h2>
        <p className={styles.text}>
          The eligibility file upload API allows partner agencies and TPAs to manage member enrollment
          at scale. Instead of adding members one-by-one, prepare a CSV file with member data and upload
          it in a single request. The system processes each row individually and returns detailed results.
        </p>
        <p className={styles.text}>
          <strong>Workflow:</strong> Prepare CSV with member data → Upload via POST with your API key →
          System validates headers and processes rows → Response includes per-row success/failure counts and error details.
        </p>
      </section>

      {/* Endpoint */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Endpoint</h2>
        <div className={styles.endpoint}>
          <div className={styles.endpointHeader}>
            <span className={styles.methodBadge}>POST</span>
            <code className={styles.endpointPath}>https://ovalcare.com/api/eligibility/upload</code>
          </div>
        </div>
        <h3 className={styles.tableTitle}>Required Headers</h3>
        <div className={styles.tableSection}>
          <div className={styles.tableWrapper}>
            <table className={styles.fieldTable}>
              <thead><tr><th>Header</th><th>Value</th></tr></thead>
              <tbody>
                <tr><td><code className={styles.fieldName}>X-API-Key</code></td><td>Your partner API key</td></tr>
                <tr><td><code className={styles.fieldName}>Content-Type</code></td><td><code className={styles.fieldType}>multipart/form-data</code></td></tr>
              </tbody>
            </table>
          </div>
        </div>
        <p className={styles.text}>
          Upload the CSV as <code className={styles.inlineCode}>multipart/form-data</code> with
          field name <code className={styles.inlineCode}>file</code>. Only <code className={styles.inlineCode}>.csv</code> files
          are accepted. Maximum file size: 100 MB.
        </p>
      </section>

      {/* Authentication */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Authentication</h2>
        <p className={styles.text}>
          All requests require a valid partner API key in the <code className={styles.inlineCode}>X-API-Key</code> header.
          API keys are generated by an Oval Care administrator via the admin portal
          and are tied to a specific agency. Contact your account manager to receive your key.
        </p>
        <CodeBlock label="Header">X-API-Key: your-api-key-here</CodeBlock>
      </section>

      {/* Rate Limiting */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Rate Limiting</h2>
        <p className={styles.text}>
          Requests are rate-limited to <strong>100 per minute</strong> per API key (60-second sliding window).
          Exceeding this limit returns a <code className={styles.inlineCode}>429</code> response. The rate limit
          is keyed to your API key, not your IP address.
        </p>
      </section>

      {/* CSV Format */}
      <section className={styles.section}>
        <div className={styles.sectionDivider}>CSV Format</div>
        <h2 className={styles.sectionTitle}>Column Reference</h2>
        <p className={styles.text}>
          The CSV must include a header row. Column names are case-insensitive. All 16 supported columns:
        </p>
        <div className={styles.tableSection}>
          <div className={styles.tableWrapper}>
            <table className={styles.fieldTable}>
              <thead><tr><th>Column</th><th>Type</th><th>Required</th><th>Description</th></tr></thead>
              <tbody>
                {CSV_COLUMNS.map(({ name, type, required, description }) => (
                  <tr key={name}>
                    <td><code className={styles.fieldName}>{name}</code></td>
                    <td><code className={styles.fieldType}>{type}</code></td>
                    <td>{required}</td>
                    <td>{description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <ul className={styles.notesList}>
          <li><strong>Required*</strong> — MEMBER_ID is required for UPDATE, CANCEL, and TERMINATE (or EMAIL as fallback identifier)</li>
          <li><strong>Required{'\u2020'}</strong> — FIRST_NAME, LAST_NAME, and EMAIL are required for ADD actions only</li>
        </ul>
      </section>

      {/* Action Types */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Action Types</h2>
        <div className={styles.tableSection}>
          <div className={styles.tableWrapper}>
            <table className={styles.fieldTable}>
              <thead><tr><th>Action</th><th>Behavior</th></tr></thead>
              <tbody>
                {ACTION_TYPES.map(({ action, description }) => (
                  <tr key={action}>
                    <td><code className={styles.fieldName}>{action}</code></td>
                    <td>{description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Example CSV */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Example CSV</h2>
        <p className={styles.text}>A sample CSV with one row per action type:</p>
        <CodeBlock label="members.csv">{EXAMPLE_CSV}</CodeBlock>
      </section>

      {/* Request Example */}
      <section className={styles.section}>
        <div className={styles.sectionDivider}>API Usage</div>
        <h2 className={styles.sectionTitle}>Request</h2>
        <CodeBlock label="curl">{CURL_EXAMPLE}</CodeBlock>
      </section>

      {/* Response Format */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Response</h2>
        <p className={styles.text}>A successful upload returns the upload ID and processing counts.</p>
        <CodeBlock label="200 OK">{SUCCESS_RESPONSE}</CodeBlock>
        <div className={styles.tableSection}>
          <h3 className={styles.tableTitle}>Response Fields</h3>
          <div className={styles.tableWrapper}>
            <table className={styles.fieldTable}>
              <thead><tr><th>Field</th><th>Type</th><th>Description</th></tr></thead>
              <tbody>
                <tr><td><code className={styles.fieldName}>success</code></td><td><code className={styles.fieldType}>boolean</code></td><td>Whether the upload was accepted and processed</td></tr>
                <tr><td><code className={styles.fieldName}>uploadId</code></td><td><code className={styles.fieldType}>string</code></td><td>Unique UUID for this upload batch</td></tr>
                <tr><td><code className={styles.fieldName}>processed</code></td><td><code className={styles.fieldType}>number</code></td><td>Total data rows processed (excludes header)</td></tr>
                <tr><td><code className={styles.fieldName}>succeeded</code></td><td><code className={styles.fieldType}>number</code></td><td>Rows that completed successfully</td></tr>
                <tr><td><code className={styles.fieldName}>failed</code></td><td><code className={styles.fieldType}>number</code></td><td>Rows that encountered errors</td></tr>
                <tr><td><code className={styles.fieldName}>skipped</code></td><td><code className={styles.fieldType}>number</code></td><td>Rows skipped (e.g., ADD with existing email, unknown action)</td></tr>
                <tr><td><code className={styles.fieldName}>errors</code></td><td><code className={styles.fieldType}>array</code></td><td>Error details per failed row: {"{ row, message }"}</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Error Responses */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Error Responses</h2>
        <CodeBlock label="400 — No file">{ERROR_RESPONSE_NO_FILE}</CodeBlock>
        <CodeBlock label="400 — Invalid headers">{ERROR_RESPONSE_HEADERS}</CodeBlock>
        <CodeBlock label="401 — Unauthorized">{ERROR_RESPONSE_AUTH}</CodeBlock>
        <CodeBlock label="429 — Rate limited">{ERROR_RESPONSE_RATE_LIMIT}</CodeBlock>
        <CodeBlock label="500 — Server error">{ERROR_RESPONSE_INTERNAL}</CodeBlock>
      </section>

      {/* HTTP Status Codes */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>HTTP Status Codes</h2>
        <div className={styles.tableSection}>
          <div className={styles.tableWrapper}>
            <table className={styles.fieldTable}>
              <thead><tr><th>Code</th><th>Description</th></tr></thead>
              <tbody>
                {ERROR_CODES.map(({ code, description }) => (
                  <tr key={code}><td><code className={styles.fieldName}>{code}</code></td><td>{description}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Upload Status Codes */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Upload Status Codes</h2>
        <p className={styles.text}>
          Each upload is tracked with a status that progresses through these states:
        </p>
        <div className={styles.tableSection}>
          <div className={styles.tableWrapper}>
            <table className={styles.fieldTable}>
              <thead><tr><th>Status</th><th>Description</th></tr></thead>
              <tbody>
                {UPLOAD_STATUSES.map(({ status, description }) => (
                  <tr key={status}><td><code className={styles.fieldName}>{status}</code></td><td>{description}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <footer className={styles.footer}>
        <p>Need help? Contact <a href="mailto:support@ovalcare.com">support@ovalcare.com</a></p>
      </footer>
    </div>
  </div>
);

export default EligibilityDocsPage;
